/**
 * Field Renderers — Unified Entry Point
 *
 * Replaces scattered switch statements across SubmissionDetailPage,
 * FormSubmissionsPage, RepeatedFormTableDisplay, and ReadOnlyFormRenderer
 * with a single registry-driven rendering system.
 *
 * Usage:
 *   import { renderDetailValue, renderTableCell, formatValue } from '../registry/renderers';
 *
 *   // In SubmissionDetailPage:
 *   renderDetailValue(field, value, { timezone, theme, attachments })
 *
 *   // In FormSubmissionsPage table:
 *   renderTableCell(field, value, { timezone, options })
 *
 *   // Plain text (export, etc.):
 *   formatValue(field, value, { timezone, options })
 */

import React from 'react';
import { getRendering, getDisplay } from '../resolvers';
import { fromPortalInternal } from '../portalAdapter';
import { formatFieldValue, isTextFormatter } from './formatters';
import { parseImageList, normalizeAttachments } from './parsers';
import {
  TextValue,
  BooleanValue,
  LinkValue,
  BadgeValue,
  BadgeListValue,
  TagListValue,
  AudioPlayerValue,
  ImageGalleryValue,
  SingleImageValue,
  MapValue,
  CoordinatesValue,
  FileListValue,
  RatingValue,
  CodeValue,
  SubformListValue,
} from './valueComponents';

/**
 * Maps registry component names (from FieldTypePresentationProvider) to
 * React components for the "detail" rendering context.
 */
const DETAIL_COMPONENTS = {
  text: TextValue,
  boolean: BooleanValue,
  link: LinkValue,
  badge: BadgeValue,
  badgeList: BadgeListValue,
  tagList: TagListValue,
  audioPlayer: AudioPlayerValue,
  imageGallery: ImageGalleryValue,
  image: SingleImageValue,
  map: MapValue,
  coordinates: CoordinatesValue,
  fileList: FileListValue,
  rating: RatingValue,
  code: CodeValue,
  subformList: SubformListValue,
  // Layout types render nothing in detail context
  divider: () => null,
  header: () => null,
  spacer: () => null,
};

/**
 * Resolve the canonical type from a portal internal field type.
 * Handles both portal internal names ('image_camera') and canonical ('Image').
 */
function resolveCanonical(field) {
  const portalType = field?.type || field?.componentId || 'input';
  return fromPortalInternal(portalType);
}

/**
 * Build the formatting context from field + options.
 */
function buildContext(field, options = {}) {
  return {
    timezone: options.timezone,
    options: field?.props?.options || options.options || [],
    display: getDisplay(resolveCanonical(field)) || {},
    precision: field?.props?.precision,
  };
}

/**
 * Wraps an arbitrary rendered value in an Advanced Field Rules `format` colour.
 * The `flag` badge is always shown alongside the colour so styling is never
 * conveyed by colour alone (WCAG AA).
 *
 * @param {Object} props
 * @param {{bg?:string,fg?:string,flag?:string}} props.fieldColor
 */
export function ColoredValue({ fieldColor, children }) {
  const { bg, fg, flag } = fieldColor || {};
  const wrapStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  };
  const chipStyle = (bg || fg) ? {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 12,
    ...(bg ? { backgroundColor: bg } : {}),
    ...(fg ? { color: fg } : {}),
  } : {};
  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 7px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    ...(bg ? { backgroundColor: bg } : {}),
    ...(fg ? { color: fg } : {}),
    border: '1px solid rgba(0,0,0,0.12)',
  };
  return (
    <span style={wrapStyle}>
      <span style={chipStyle}>{children}</span>
      {flag ? <span style={badgeStyle}>{flag}</span> : null}
    </span>
  );
}

/**
 * Render a field value for the "detail" context (submission detail page).
 * Returns a React element.
 *
 * @param {Object} field - Field definition (type, props, id/key)
 * @param {*} value - Raw field value
 * @param {Object} options - { timezone, theme, attachments, renderChildren, fieldColor }
 * @returns {React.ReactElement}
 */
export function renderDetailValue(field, value, options = {}) {
  const canonical = resolveCanonical(field);
  const rendering = getRendering(canonical, 'detail');
  const display = getDisplay(canonical) || {};
  const ctx = buildContext(field, options);

  const componentName = rendering.component || 'text';
  const Component = DETAIL_COMPONENTS[componentName] || TextValue;

  const formatted = isTextFormatter(display.formatter)
    ? formatFieldValue(display.formatter, value, ctx)
    : value;

  const rendered = (
    <Component
      value={value}
      formatted={formatted}
      field={field}
      emptyValue={display.emptyValue || '—'}
      theme={options.theme}
      attachments={normalizeAttachments(options.attachments)}
      renderChildren={options.renderChildren}
      onImageClick={options.onImageClick}
    />
  );

  // Apply an Advanced Field Rules `format` colour when supplied.
  const fc = options.fieldColor;
  if (fc && (fc.bg || fc.fg || fc.flag)) {
    return <ColoredValue fieldColor={fc}>{rendered}</ColoredValue>;
  }
  return rendered;
}

/**
 * Table-context summaries for component-rendered field types.
 * These types can't render rich components in a table cell, so we produce
 * a meaningful text summary instead of showing raw filenames.
 */
const TABLE_SUMMARIES = {
  imageThumb: (value) => {
    const items = parseImageList(value);
    if (items.length === 0) return null;
    return items.length === 1 ? '1 image' : `${items.length} images`;
  },
  imageCount: (value) => {
    const items = parseImageList(value);
    return items.length > 0 ? `${items.length}` : null;
  },
  duration: (value) => {
    if (!value) return null;
    return '[Audio]';
  },
  fileCount: (value) => {
    const items = Array.isArray(value) ? value : (value ? [value] : []);
    if (items.length === 0) return null;
    return items.length === 1 ? '1 file' : `${items.length} files`;
  },
  count: (value) => {
    const items = Array.isArray(value) ? value : [];
    return items.length > 0 ? `${items.length}` : null;
  },
};

/**
 * Render a field value for the "table" context (list views, repeated form tables).
 * Returns a plain string suitable for table cells — unless an Advanced Field
 * Rules `fieldColor` is supplied, in which case a coloured React element is
 * returned (callers render the result inside JSX either way).
 *
 * @param {Object} field - Field definition with type/props
 * @param {*} value - Raw field value
 * @param {Object} options - { timezone, options, fieldColor }
 * @returns {string|React.ReactElement}
 */
export function renderTableCell(field, value, options = {}) {
  const canonical = resolveCanonical(field);
  const display = getDisplay(canonical) || {};
  const tableRendering = getRendering(canonical, 'table');
  const ctx = buildContext(field, options);

  let text;
  // Media/complex types get a text summary in table context
  const tableSummary = TABLE_SUMMARIES[tableRendering.component];
  if (tableSummary) {
    const result = tableSummary(value);
    text = result ?? (display.emptyValue || '—');
  } else {
    const formatted = formatFieldValue(display.formatter || 'string', value, ctx);
    text = formatted ?? (display.emptyValue || '—');
  }

  // Apply an Advanced Field Rules `format` colour when supplied.
  const fc = options.fieldColor;
  if (fc && (fc.bg || fc.fg || fc.flag)) {
    return <ColoredValue fieldColor={fc}>{text}</ColoredValue>;
  }
  return text;
}

/**
 * Format a field value as plain text (for export, clipboard, etc.).
 * Same as renderTableCell but explicitly named for non-rendering contexts.
 *
 * @param {Object} field - Field definition with type/props
 * @param {*} value - Raw field value
 * @param {Object} options - { timezone, options }
 * @returns {string}
 */
export function formatValue(field, value, options = {}) {
  return renderTableCell(field, value, options);
}

export { DETAIL_COMPONENTS };
